import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";

import { designStore, generalConfigStore, setCategoryDisplay } from "@store";

import {
    DisplayStyleSettings,
    DisplayStyleSetters,
    DisplayStyleValues,
} from "../shared/DisplayStyleSettings";
import { makeResolveColor } from "../shared/displayColor";
import { persistOrderTakingPatch } from "../shared/persistOrderTaking";

// Noms réels des champs persistés côté template pour les catégories (Mode 2).
const CATEGORY_PERSIST_KEYS: Record<keyof DisplayStyleValues, string> = {
    backgroundColor: "categoryDisplayBackgroundColor",
    backgroundOpacity: "categoryDisplayBackgroundOpacity",
    selectedTextColor: "categoryDisplaySelectedTextColor",
    borderColor: "categoryDisplayBorderColor",
    borderStyle: "categoryDisplayBorderStyle",
    svgBorder: "categoryDisplaySvgBorder",
    svgBorderName: "categoryDisplaySvgBorderName",
};

// Réglages d'affichage de la catégorie sélectionnée (Mode 2). Simple wrapper sur
// le composant générique DisplayStyleSettings (en miroir de ProductDisplaySettings) :
// le générique porte color pickers a11y, upload SVG validé/purgé (DOMPurify),
// switch transparence, styles de bordure. Ici on ne fait que brancher les valeurs
// du store, le setter patch setCategoryDisplay et les clés de persistance catégorie.
export function CategoryDisplayMode2Settings(): JSX.Element {
    const { t } = useTranslation();

    const {
        project: { template },
    } = useSnapshot(generalConfigStore);

    const {
        categoryDisplayBackgroundColor,
        categoryDisplayBackgroundOpacity,
        categoryDisplaySelectedTextColor,
        categoryDisplayBorderColor,
        categoryDisplaySvgBorder,
        categoryDisplaySvgBorderName,
        categoryDisplayBorderStyle,
    } = useSnapshot(designStore);

    // `template.content` est typé via un index signature union (ProjectContentType) :
    // on annote explicitement la frontière vers GeneralDesignType (le type réel
    // de la slot generalDesign) attendu par makeResolveColor.
    const resolveColor = makeResolveColor(
        template.content.generalDesign as GeneralDesignType
    );

    const values: DisplayStyleValues = {
        backgroundColor: categoryDisplayBackgroundColor,
        backgroundOpacity: categoryDisplayBackgroundOpacity,
        selectedTextColor: categoryDisplaySelectedTextColor,
        borderColor: categoryDisplayBorderColor,
        borderStyle: categoryDisplayBorderStyle,
        svgBorder: categoryDisplaySvgBorder,
        svgBorderName: categoryDisplaySvgBorderName,
    };

    // Setters branchés sur le setter patch unique setCategoryDisplay : chaque
    // champ écrit sous sa clé store réelle (CATEGORY_PERSIST_KEYS).
    const setters: DisplayStyleSetters = {
        setBackgroundColor: (v) =>
            setCategoryDisplay({ [CATEGORY_PERSIST_KEYS.backgroundColor]: v }),
        setBackgroundOpacity: (v) =>
            setCategoryDisplay({
                [CATEGORY_PERSIST_KEYS.backgroundOpacity]: v,
            }),
        setSelectedTextColor: (v) =>
            setCategoryDisplay({
                [CATEGORY_PERSIST_KEYS.selectedTextColor]: v,
            }),
        setBorderColor: (v) =>
            setCategoryDisplay({ [CATEGORY_PERSIST_KEYS.borderColor]: v }),
        setBorderStyle: (v) =>
            setCategoryDisplay({ [CATEGORY_PERSIST_KEYS.borderStyle]: v }),
        setSvgBorder: (v) =>
            setCategoryDisplay({ [CATEGORY_PERSIST_KEYS.svgBorder]: v }),
        setSvgBorderName: (v) =>
            setCategoryDisplay({ [CATEGORY_PERSIST_KEYS.svgBorderName]: v }),
    };

    return (
        <DisplayStyleSettings
            testIdPrefix="order-taking-menu-mode-2"
            backgroundColorLabel={t("Selection background color")}
            textColorLabel={t("Category title color")}
            svgFrameTitle={t("Selected category frame")}
            svgFrameSubtitle={t(
                "Insert an SVG frame for the selected category"
            )}
            svgFrameTitleBold={false}
            persistKeys={CATEGORY_PERSIST_KEYS}
            resolveColor={resolveColor}
            persist={persistOrderTakingPatch}
            values={values}
            setters={setters}
        />
    );
}
